import { Outlet, Link, useLocation, useNavigate, Routes, Route } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Home, ClipboardList, Camera, User, Settings } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import MobileDashboardPage from '../../pages/mobile/MobileDashboardPage'
import MobileTasksPage from '../../pages/mobile/MobileTasksPage'
import MobileProjectsPage from '../../pages/mobile/MobileProjectsPage'
import MobileWorkerDashboard from '../../pages/mobile/MobileWorkerDashboard'
import MobileJobDetailPage from '../../pages/mobile/MobileJobDetailPage'

/**
 * Mobile-First Layout for Installer Mode
 * Bottom Navigation Bar instead of Sidebar
 */
function MobileLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const [installPrompt, setInstallPrompt] = useState(null)
  const [toonInstallBanner, setToonInstallBanner] = useState(false)

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault()
      setInstallPrompt(e)
      setToonInstallBanner(true)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const installeerApp = async () => {
    if (!installPrompt) return
    installPrompt.prompt()
    const { outcome } = await installPrompt.userChoice
    if (outcome === 'accepted') setToonInstallBanner(false)
  }

  const navigation = [
    { name: t('mobile.navigation.tasks', { defaultValue: 'Mijn Planning' }), href: '/mobile/worker', icon: ClipboardList },
    { name: t('mobile.navigation.projects', { defaultValue: 'Projects' }), href: '/mobile/projects', icon: Home },
    { name: t('mobile.navigation.camera', { defaultValue: 'Camera' }), href: '/mobile/camera', icon: Camera },
    { name: t('mobile.navigation.profile', { defaultValue: 'Profile' }), href: '/mobile/profile', icon: User },
    { name: t('mobile.navigation.settings', { defaultValue: 'Settings' }), href: '/mobile/settings', icon: Settings }
  ]

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* PWA Install Banner */}
      {toonInstallBanner && (
        <div className="bg-blue-700 text-white px-4 py-2 flex items-center justify-between text-sm">
          <span>Installeer Orion op je GSM</span>
          <div className="flex gap-2">
            <button
              onClick={installeerApp}
              className="bg-white text-blue-700 px-3 py-1 rounded-full text-xs font-semibold"
            >
              Installeer
            </button>
            <button
              onClick={() => setToonInstallBanner(false)}
              className="text-blue-200 text-lg leading-none"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-20">
        <Routes>
          <Route index element={<MobileWorkerDashboard />} />
          <Route path="worker" element={<MobileWorkerDashboard />} />
          <Route path="jobs/:id" element={<MobileJobDetailPage />} />
          <Route path="tasks" element={<MobileTasksPage />} />
          <Route path="projects" element={<MobileProjectsPage />} />
          <Route path="projects/:id" element={<div className="p-4 text-gray-500">Project detail — binnenkort beschikbaar</div>} />
          <Route path="camera" element={
            <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8 text-center">
              <span className="text-5xl mb-4">{'\uD83D\uDCF7'}</span>
              <p className="text-gray-600 text-sm">Foto's worden ge\u00fcpload via de job detail pagina.</p>
              <Link to="/mobile/worker" className="mt-4 text-blue-600 text-sm font-medium">Naar mijn planning &rarr;</Link>
            </div>
          } />
          <Route path="profile" element={<MobileProfielPagina />} />
          <Route path="settings" element={
            <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8 text-center">
              <span className="text-5xl mb-4">{'\u2699\uFE0F'}</span>
              <p className="text-gray-600 text-sm">Instellingen komen binnenkort.</p>
            </div>
          } />
        </Routes>
      </main>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
            
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <Icon className="w-6 h-6 mb-1" />
                <span className="text-xs font-medium">{item.name}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

function MobileProfielPagina() {
  const user = useAuthStore(s => s.user)
  const logout = useAuthStore(s => s.logout)
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="bg-white px-4 pt-6 pb-4 border-b border-gray-100">
        <h1 className="text-xl font-bold text-gray-900">Profiel</h1>
      </div>
      <div className="flex-1 p-4 space-y-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Naam</span>
            <span className="font-medium text-gray-900">{user?.gebruikersnaam}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Rol</span>
            <span className="font-medium text-gray-900">{user?.role ?? '\u2014'}</span>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full bg-red-50 text-red-600 font-medium py-3 rounded-2xl text-sm border border-red-100"
        >
          Afmelden
        </button>
      </div>
    </div>
  )
}

export default MobileLayout
